import { useState, useCallback, useRef } from 'react';
import { TILE_SIZE } from '../../../game/events';

/**
 * The world being edited, and the ability to take it back.
 *
 * ONE OWNER FOR THE EDIT STATE. The canvas holds nothing but its drag; every
 * change goes through here. That is what makes undo a single stack rather than
 * something split between a canvas, a palette and a page — the version of undo
 * that works for the brush and silently misses spawn points.
 *
 * A STROKE IS ONE UNDO, not one per tile. Dragging across forty tiles and then
 * pressing undo forty times is not undo, it is punishment. `isStart` on a paint
 * marks where a stroke begins; everything after it mutates the same snapshot.
 *
 * LAYERS ARE COPIED ON WRITE, not mutated. React compares by reference, and an
 * in-place splice into `layers.walls` would repaint nothing — the classic
 * "my edit only shows up when I click something else".
 */

/** How many steps back you can go. Each entry is up to three tile arrays, so
 *  50 on an 80x60 world is roughly 3MB — invisible next to the page itself. */
const UNDO_LIMIT = 50;

export function useWorldDraft() {
  const [world, setWorld] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);

  /** True while a drag is in progress, so mid-stroke changes do not each push
   *  their own undo entry. A ref rather than state: it changes on every
   *  mousemove and must not re-render. */
  const stroking = useRef(false);

  const snapshot = useCallback((current) => ({
    ...current,
    layers: {
      floor: [...current.layers.floor],
      walls: [...current.layers.walls],
      above: [...current.layers.above],
    },
    spawns: [...current.spawns],
    portals: [...current.portals],
    signs: [...current.signs],
    zones: [...(current.zones || [])],
  }), []);

  /** Load a world from the server. Clears undo — history from a world you are
   *  no longer looking at would restore the wrong geometry. */
  const load = useCallback((payload) => {
    setWorld({
      name: payload.name,
      title: payload.title,
      widthTiles: payload.widthTiles,
      heightTiles: payload.heightTiles,
      layers: payload.layers,
      spawns: payload.spawns || [],
      portals: payload.portals || [],
      signs: payload.signs || [],
      zones: payload.zones || [],
    });
    setUndoStack([]);
    setSelectedZoneId(null);
    setDirty(false);
  }, []);

  /** Apply a change, pushing an undo entry unless we are mid-stroke. */
  const change = useCallback((mutate, { newStroke = true } = {}) => {
    setWorld((current) => {
      if (!current) return current;

      if (newStroke) {
        setUndoStack((stack) => [...stack, snapshot(current)].slice(-UNDO_LIMIT));
      }

      return mutate(snapshot(current));
    });
    setDirty(true);
  }, [snapshot]);

  const paint = useCallback(({
    tx, ty, layer, gid, isStart,
  }) => {
    if (isStart) stroking.current = false;

    change((next) => {
      const data = [...next.layers[layer]];
      data[ty * next.widthTiles + tx] = gid;
      return { ...next, layers: { ...next.layers, [layer]: data } };
    }, { newStroke: !stroking.current });

    // Everything after the first tile of a drag folds into the same entry.
    stroking.current = true;
  }, [change]);

  const place = useCallback(({
    tool, x, y, tx, ty,
  }) => {
    change((next) => {
      if (tool === 'spawn') {
        return { ...next, spawns: [...next.spawns, { x, y }] };
      }
      if (tool === 'sign') {
        // eslint-disable-next-line no-alert
        const text = window.prompt('What does the sign say?');
        if (!text) return next;
        return { ...next, signs: [...next.signs, { x, y, text: text.slice(0, 120) }] };
      }
      if (tool === 'portal') {
        // eslint-disable-next-line no-alert
        const toMap = window.prompt('Which world does this lead to?');
        if (!toMap) return next;
        // Snapped to the tile grid and two tiles wide, because a portal you can
        // see but not walk into is worse than no portal — and one tile is
        // narrower than a player.
        return {
          ...next,
          portals: [...next.portals, {
            x: tx * TILE_SIZE,
            y: ty * TILE_SIZE,
            width: TILE_SIZE * 2,
            height: TILE_SIZE * 2,
            toMap: toMap.trim().toLowerCase(),
            toSpawn: null,
            label: toMap.trim(),
          }],
        };
      }
      return next;
    });
  }, [change]);

  /** Remove whatever marker is nearest the click, within about a tile. Placing
   *  is a click and so is removing — a separate delete tool per kind would be
   *  four more buttons for one idea. */
  const removeNear = useCallback(({ x, y }) => {
    change((next) => {
      const near = (p) => Math.abs(p.x - x) <= TILE_SIZE && Math.abs(p.y - y) <= TILE_SIZE;

      const spawnAt = next.spawns.findIndex(near);
      if (spawnAt >= 0) {
        return { ...next, spawns: next.spawns.filter((_, i) => i !== spawnAt) };
      }

      const signAt = next.signs.findIndex(near);
      if (signAt >= 0) {
        return { ...next, signs: next.signs.filter((_, i) => i !== signAt) };
      }

      const portalAt = next.portals.findIndex((p) => (
        x >= p.x && x <= p.x + p.width && y >= p.y && y <= p.y + p.height
      ));
      if (portalAt >= 0) {
        return { ...next, portals: next.portals.filter((_, i) => i !== portalAt) };
      }

      return next;
    });
  }, [change]);

  /**
   * A building, from a drag across the grid.
   *
   * Given a door straight away — on the north side, two tiles wide, which is
   * the minimum a player fits through. A building created without one is
   * sealed, and every single new zone would arrive with a warning attached.
   */
  const addZone = useCallback(({
    tx, ty, w, h,
  }) => {
    // Ids only need to be unique within one world and stable while it is open.
    // A counter off the current length would collide after a delete.
    const id = `z${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

    change((next) => ({
      ...next,
      zones: [...next.zones, {
        id,
        kind: 'building',
        label: '',
        x: tx,
        y: ty,
        w,
        h,
        doors: [{ side: 'n', at: 1, span: 2 }],
      }],
    }));

    setSelectedZoneId(id);
    return id;
  }, [change]);

  const updateZone = useCallback((id, patch) => {
    change((next) => ({
      ...next,
      zones: next.zones.map((z) => (z.id === id ? { ...z, ...patch } : z)),
    }));
  }, [change]);

  const removeZone = useCallback((id) => {
    change((next) => ({ ...next, zones: next.zones.filter((z) => z.id !== id) }));
    setSelectedZoneId((current) => (current === id ? null : current));
  }, [change]);

  /** The zone under a point, topmost first — later zones are drawn over
   *  earlier ones, so clicking should pick what you can see. */
  const zoneAt = useCallback((tx, ty) => {
    if (!world) return null;
    return [...world.zones].reverse().find(
      (z) => tx >= z.x && ty >= z.y && tx < z.x + z.w && ty < z.y + z.h,
    ) || null;
  }, [world]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      setWorld(stack[stack.length - 1]);
      setDirty(true);
      return stack.slice(0, -1);
    });
  }, []);

  /** After a successful save — the server now agrees with what is on screen. */
  const markSaved = useCallback(() => setDirty(false), []);

  return {
    world,
    dirty,
    canUndo: undoStack.length > 0,
    selectedZone: world?.zones.find((z) => z.id === selectedZoneId) || null,
    load,
    paint,
    place,
    removeNear,
    addZone,
    updateZone,
    removeZone,
    zoneAt,
    selectZone: setSelectedZoneId,
    undo,
    markSaved,
  };
}

export default useWorldDraft;
